import { Typography } from "@mui/material";
import ChecklistAnswerSubtypeSummary from "./summary/ChecklistAnswerSubtypeSummary";
import EvaluationStarsSubtypeSummary from "./summary/EvaluationStarsSubtypeSummary";
import EvaluationThumbsSubtypeSummary from "./summary/EvaluationThumbsSubtypeSummary";
import PictureUploadSubtypeSummary from "./summary/PictureUploadSubtypeSummary";
import TextAnswerSubtypeSummary from "./summary/TextAnswerSubtypeSummary";
import CellBox from "../../../../../components/common/table/CellBox";
import { useMemo } from "react";
import { ActionModule } from "../../../../../types/actionModule/actionModule";
import { ActionModuleInstanceResponse } from "../../../../../types/actionModule/actionModuleInstance";
import { BaseModuleSubtypeResponse, Subtype } from "../../../../../types/actionModule/baseModuleSubtype";
import { BaseSubtypeDataModel } from "../../../../../types/actionModule/subtypeData";
import { ChecklistAnswerSubtypeResponse, ChecklistAnswerSubtypeDataModel } from "../../../../../types/actionModule/subtypes/checklistAnswerModels";
import { EvaluationStarsSubtypeDataModel } from "../../../../../types/actionModule/subtypes/evaluationStarModels";
import { EvaluationThumbsSubtypeDataModel } from "../../../../../types/actionModule/subtypes/evaluationThumbsModels";
import { PictureUploadSubtypeResponse, PictureUploadSubtypeDataModel } from "../../../../../types/actionModule/subtypes/pictureUploadModels";
import { TextAnswerSubtypeDataModel } from "../../../../../types/actionModule/subtypes/textAnswerModels";
import { useTranslation } from "react-i18next";
import { MultiPictureUploadSubtypeDataModel, MultiPictureUploadSubtypeResponse } from "../../../../../types/actionModule/subtypes/multiPictureUploadModels";
import MultiPictureUploadSubtypeSummary from "./summary/MultiPictureUploadSubtypeSummary";
import { SurveyAppointmentSubtypeDataModel } from "../../../../../types/actionModule/subtypes/surveyAppointmentModels";
import SurveyAppointmentSubtypeSummary from "./summary/SurveyAppointmentSubtypeSummary";

interface Props {
    actionModule: ActionModule;
    subtype: BaseModuleSubtypeResponse;
    instance: ActionModuleInstanceResponse;
}

export default function SubtypeSummaryCell(props: Props) {
    const { t } = useTranslation();

    const subtypeData = useMemo(
        () => props.instance.subtypeData.find(d => d.moduleSubtypeId == props.subtype.id),
        [props.instance, props.subtype]
    );

    if (subtypeData === undefined) {
        return <CellBox><Typography>{t("incentivationPage.noData")}</Typography></CellBox>;
    }

    return <CellBox>
        <SubtypeSummary
            subtype={props.subtype}
            subtypeData={subtypeData} />
    </CellBox>;
}

interface SubtypeSummaryProps {
    subtype: BaseModuleSubtypeResponse;
    subtypeData: BaseSubtypeDataModel;
}

function SubtypeSummary(props: SubtypeSummaryProps) {
    switch (props.subtype.subtype) {
        case Subtype.ChecklistAnswerModuleSubtype:
            return <ChecklistAnswerSubtypeSummary
                subtype={props.subtype as ChecklistAnswerSubtypeResponse}
                subtypeData={props.subtypeData as ChecklistAnswerSubtypeDataModel} />
        case Subtype.EvaluationStarsModuleSubtype:
            return <EvaluationStarsSubtypeSummary
                subtype={props.subtype}
                subtypeData={props.subtypeData as EvaluationStarsSubtypeDataModel} />
        case Subtype.EvaluationThumbsModuleSubtype:
            return <EvaluationThumbsSubtypeSummary
                subtype={props.subtype}
                subtypeData={props.subtypeData as EvaluationThumbsSubtypeDataModel} />
        case Subtype.MultiPictureUploadModuleSubtype:
            return <MultiPictureUploadSubtypeSummary
                subtype={props.subtype as MultiPictureUploadSubtypeResponse}
                subtypeData={props.subtypeData as MultiPictureUploadSubtypeDataModel} />
        case Subtype.PictureUploadModuleSubtype:
            return <PictureUploadSubtypeSummary
                subtype={props.subtype as PictureUploadSubtypeResponse}
                subtypeData={props.subtypeData as PictureUploadSubtypeDataModel} />
        case Subtype.TextAnswerModuleSubtype:
            return <TextAnswerSubtypeSummary
                subtype={props.subtype as BaseModuleSubtypeResponse}
                subtypeData={props.subtypeData as TextAnswerSubtypeDataModel} />
        case Subtype.SurveyAppointmentModuleSubtype:
            return <SurveyAppointmentSubtypeSummary
                subtype={props.subtype as BaseModuleSubtypeResponse}
                subtypeData={props.subtypeData as SurveyAppointmentSubtypeDataModel} />
        case Subtype.AnniversaryBonusModuleSubtype:
        case Subtype.BirthdayBonusModuleSubtype:
        case Subtype.SignatureControlModuleSubtype:
        case Subtype.PresenceQuotaModuleSubtype:
        case Subtype.WelcomeBonisoftModuleSubtype:
        default:
            return <></>
    }
}
